import React, {useEffect, useReducer, useRef, useState} from "react"
import StandardList from "../../../common/listing/list.component"
import {Row as RowType} from "../../../common/listing/ListingTypes"
import {Button, InputGroup, Modal, Row} from "react-bootstrap"
import {injectIntl, defineMessages} from "react-intl"
import {Intl} from "../../../state/helpers/IntlProps"
import Icon from "../../../common/icon/icon.component"
import {
    initialMatrixAxisOption,
    MatrixAxisOptionDto
} from "../../../state/product/matrix/matrix-axis-option/MatrixAxisOption"
import {MatrixAxis} from "../../../state/product/matrix/Matrix"
import {produce} from "immer"
import {MATRIX_AXIS_TYPE} from "../../../state/product/Types"

const uuidv4 = require("uuid/v4")

const messages = defineMessages({
    name: {
        id: "product.variant.component.variant_edit_axis_option_modal.option_name",
        defaultMessage: "Option name",
    },
    values: {
        id: "product.variant.component.variant_edit_axis_option_modal.option_values",
        defaultMessage: "Option values",
    },
    done: {
        id: "product.variant.component.variant_edit_axis_option_modal.done",
        defaultMessage: "Done",
    },
    add_a_value: {
        id: "product.variant.component.variant_edit_axis_option_modal.add_a_value",
        defaultMessage: "Add a value",
    },
    add: {
        id: "product.variant.component.variant_edit_axis_option_modal.add",
        defaultMessage: "Add",
    },
    title: {
        id: "product.variant.component.variant_edit_axis_option_modal.title",
        defaultMessage: "Edit options",
    }
})

const MATRIX_AXIS_UPDATED = "matrix-axis-updated"
type MATRIX_AXIS_UPDATED = typeof MATRIX_AXIS_UPDATED

const MATRIX_AXIS_OPTION_UPDATED = "matrix-axis-option-updated"
type MATRIX_AXIS_OPTION_UPDATED = typeof MATRIX_AXIS_OPTION_UPDATED

const MATRIX_AXIS_OPTIONS_ORDERED = "matrix-axis-options-ordered"
type MATRIX_AXIS_OPTIONS_ORDERED = typeof MATRIX_AXIS_OPTIONS_ORDERED

const MATRIX_AXIS_OPTION_DELETED = "matrix-axis-option-deleted"
type MATRIX_AXIS_OPTION_DELETED = typeof MATRIX_AXIS_OPTION_DELETED

const MATRIX_AXIS_OPTION_CREATED = "matrix-axis-option-created"
type MATRIX_AXIS_OPTION_CREATED = typeof MATRIX_AXIS_OPTION_CREATED


type Props = {
    intl: Intl
    axis: MatrixAxis
    onCloseCallback: () => void
    onUpdateCallback: (axis: MatrixAxis) => void
    onDeleteCallback: () => void
}

type MatrixAxisOptionPayload = {
    optionId: string
    name: string
}

type DeletedMatrixAxisOptionPayload = {
    optionId: string
}

type MatrixAxisPayload = {
    axisType: MATRIX_AXIS_TYPE
    name: string
}

type MatrixAxisOrderedOptionsPayload = {
    orderedOptions: MatrixAxisOptionDto[]
}

type action =
    | { type: MATRIX_AXIS_UPDATED, payload: MatrixAxisPayload }
    | { type: MATRIX_AXIS_OPTION_UPDATED, payload: MatrixAxisOptionPayload }
    | { type: MATRIX_AXIS_OPTIONS_ORDERED, payload: MatrixAxisOrderedOptionsPayload }
    | { type: MATRIX_AXIS_OPTION_DELETED, payload: DeletedMatrixAxisOptionPayload }
    | { type: MATRIX_AXIS_OPTION_CREATED, payload: MatrixAxisPayload }


const NewMatrixOption = (payload: MatrixAxisPayload): MatrixAxisOptionDto => {
    return produce(initialMatrixAxisOption, draft => {
        draft.id = uuidv4()
        draft.attributes.name = payload.name
    })
}

const AxisReducer = (state: MatrixAxis, action: action) => {
    switch (action.type) {
    case MATRIX_AXIS_UPDATED:
        return produce(state, draft => {
            draft.name = action.payload.name
        })
    case MATRIX_AXIS_OPTION_CREATED:
        return produce(state, draft => {
            draft.options.push(NewMatrixOption(action.payload))
        })
    case MATRIX_AXIS_OPTION_UPDATED:
        return produce(state, draft => {
            draft.options =
                    draft.options.map(o => {
                        if (action.payload.optionId !== o.id) {
                            return o
                        }
                        return produce(o, draft => {
                            draft.attributes.name = action.payload.name
                        })
                    })
        })
    case MATRIX_AXIS_OPTION_DELETED:
        return produce(state, draft => {
            draft.options = draft.options.filter(o => o.id !== action.payload.optionId)
        })
    case MATRIX_AXIS_OPTIONS_ORDERED:
        return produce(state, draft => {
            draft.options = action.payload.orderedOptions
        })
    default:
        return state
    }
}


const EditMatrixAxisOptionModal = (props: Props) => {
    const newOptionInputRef = useRef() as React.MutableRefObject<HTMLInputElement>
    const [state, dispatch] = useReducer(AxisReducer, props.axis)
    const [key, setKey] = useState(0)

    useEffect(() => {
        setKey(key + 1)
    }, [state.options.map(o => o.id).join("")])

    const rows: RowType<MatrixAxisOptionDto>[] = state.options.map(o => {
        return {
            rowId: o.id,
            metadata: o,
            data: [
                <input
                    key={0}
                    className='form-control'
                    defaultValue={o.attributes.name}
                    onChange={e => {
                        dispatch({
                            type: MATRIX_AXIS_OPTION_UPDATED,
                            payload: {
                                optionId: o.id,
                                name: e.target.value,
                            }
                        })
                    }}
                />,
                <a key={1} className={"text-danger"} href={"#"}
                    onClick={e => {
                        e.preventDefault()
                        dispatch({
                            type: MATRIX_AXIS_OPTION_DELETED,
                            payload: {optionId: o.id},
                        }
                        )
                    }}>
                    <Icon value={"trash"}/>
                </a>
            ]
        }
    })

    const disabled = state.name === ""
        || state.options.length === 0
        || state.options.filter(o => o.attributes.name === "").length > 0

    return (
        <Modal show={true} onHide={props.onCloseCallback} backdrop={"static"}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {props.intl.formatMessage(messages["title"])}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div>
                    <label htmlFor='option-name'>
                        {props.intl.formatMessage(messages["name"])}
                    </label>
                    <Row className={"pl-3"}>
                        <input name='option-name' id='option-name' defaultValue={state.name}
                            className='col-10 form-control'
                            onBlur={e => dispatch(
                                {
                                    type: MATRIX_AXIS_UPDATED,
                                    payload: {
                                        axisType: props.axis.type,
                                        name: e.target.value
                                    }
                                })}
                        />

                        <a className={"text-danger ml-3 mt-2"} href={"#"}
                            onClick={e => {
                                e.preventDefault()
                                props.onDeleteCallback()
                            }}>
                            <Icon value={"trash"}/>
                        </a>
                    </Row>
                </div>

                <hr/>

                <div>
                    <label>
                        {props.intl.formatMessage(messages["values"])}
                    </label>

                    <StandardList
                        key={key}
                        sortable={true}
                        className={"p-0"}
                        didSorted={orderedRows => {
                            dispatch({
                                type: MATRIX_AXIS_OPTIONS_ORDERED,
                                payload: {
                                    orderedOptions: orderedRows.map((r, index) => {
                                        return produce(r.metadata, draft => {
                                            draft.attributes.orderBy = index
                                        })
                                    })
                                }
                            })
                        }}
                        rows={rows}
                        smallLine={true}/>
                </div>

                <Modal.Dialog className={"pt-2"}>
                    <Modal.Body>
                        <InputGroup>

                            <input ref={newOptionInputRef}
                                onKeyPress={e => {
                                    if (newOptionInputRef.current.value !== "" && e.code == "Enter") {
                                        dispatch(
                                            {
                                                type: MATRIX_AXIS_OPTION_CREATED,
                                                payload: {
                                                    axisType: props.axis.type,

                                                    name: newOptionInputRef.current.value
                                                }
                                            })
                                        newOptionInputRef.current.value = ""
                                    }
                                }}
                                className='form-control'
                                placeholder={props.intl.formatMessage(messages["add_a_value"])}/>
                            <div className='input-group-append'>
                                <Button type='button' variant='success'  
                                    onClick={() => {
                                        if (newOptionInputRef.current.value === "") {
                                            return
                                        }
                                        dispatch(
                                            {
                                                type: MATRIX_AXIS_OPTION_CREATED,
                                                payload: {
                                                    axisType: props.axis.type,
                                                    name: newOptionInputRef.current.value
                                                }
                                            })
                                        newOptionInputRef.current.value = ""
                                    }}>
                                    <h5 className='mb-0'>+</h5>
                                </Button>
                            </div>

                        </InputGroup>
                    </Modal.Body>
                </Modal.Dialog>
            </Modal.Body>

            <Modal.Footer>
                <Button disabled={disabled} type='button' variant='primary'
                    onClick={() => props.onUpdateCallback(state)}>
                    {props.intl.formatMessage(messages["done"])}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}


export default injectIntl(EditMatrixAxisOptionModal)


// WEBPACK FOOTER //
// ./src/product/variant/modal/EditMatrixAxisOptionModal.tsx